import type { ReactElement } from "react";

export interface IconProps {
  name: string;
  size?: number;
  color?: string;
  strokeWidth?: number;
}

export function Icon({
  name,
  size = 28,
  color = "currentColor",
  strokeWidth = 2,
}: IconProps): ReactElement {
  const common = {
    width: size,
    height: size,
    viewBox: "0 0 32 32",
    fill: "none" as const,
    stroke: color,
    strokeWidth,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
  };
  const paths: Record<string, ReactElement> = {
    gamepad: (
      <>
        <rect x="4" y="10" width="24" height="14" rx="5" />
        <circle cx="11" cy="17" r="1.5" fill={color} />
        <circle cx="21" cy="17" r="1.5" fill={color} />
      </>
    ),
    bike: (
      <>
        <circle cx="8" cy="22" r="5" />
        <circle cx="24" cy="22" r="5" />
        <path d="M8 22 L16 12 L22 22 M16 12 L20 12 M12 8 L18 8" />
      </>
    ),
    ticket: (
      <>
        <path d="M5 11 C7 11 7 13 7 14 C7 16 5 16 5 18 L5 22 L27 22 L27 18 C25 18 25 16 25 14 C25 13 25 11 27 11 L27 7 L5 7 Z" />
        <path d="M14 9 L14 24" strokeDasharray="2 2" />
      </>
    ),
    palette: (
      <>
        <path d="M16 4 C22 4 28 9 28 15 C28 19 25 20 22 20 L20 20 C18 20 17 21 17 23 C17 26 15 28 12 28 C7 28 4 23 4 17 C4 10 9 4 16 4 Z" />
        <circle cx="11" cy="12" r="1.5" fill={color} stroke="none" />
        <circle cx="16" cy="10" r="1.5" fill={color} stroke="none" />
        <circle cx="22" cy="13" r="1.5" fill={color} stroke="none" />
      </>
    ),
    skate: (
      <>
        <path d="M4 18 L28 18 L28 22 L4 22 Z" />
        <circle cx="9" cy="26" r="2" />
        <circle cx="23" cy="26" r="2" />
        <path d="M8 18 L10 12 L22 12 L24 18" />
      </>
    ),
    book: (
      <>
        <path d="M6 6 L15 8 L15 26 L6 24 Z" />
        <path d="M26 6 L17 8 L17 26 L26 24 Z" />
      </>
    ),
    blocks: (
      <>
        <rect x="5" y="14" width="10" height="10" rx="1" />
        <rect x="17" y="14" width="10" height="10" rx="1" />
        <rect x="11" y="4" width="10" height="10" rx="1" />
        <circle cx="10" cy="14" r="1" fill={color} stroke="none" />
        <circle cx="22" cy="14" r="1" fill={color} stroke="none" />
        <circle cx="16" cy="4" r="1" fill={color} stroke="none" />
      </>
    ),
    ball: (
      <>
        <circle cx="16" cy="16" r="11" />
        <path d="M16 5 L16 27 M5 16 L27 16 M8 8 L24 24 M24 8 L8 24" />
      </>
    ),
    camera: (
      <>
        <path d="M4 10 L10 10 L12 7 L20 7 L22 10 L28 10 L28 24 L4 24 Z" />
        <circle cx="16" cy="16" r="5" />
      </>
    ),
    tent: (
      <>
        <path d="M16 4 L4 26 L28 26 Z" />
        <path d="M16 4 L16 26 M12 26 L16 20 L20 26" />
      </>
    ),
    laptop: (
      <>
        <rect x="6" y="7" width="20" height="14" rx="1.5" />
        <path d="M3 24 L29 24 L27 21 L5 21 Z" />
      </>
    ),
    car: (
      <>
        <path d="M5 20 L7 13 C7.5 12 8.5 11 10 11 L22 11 C23.5 11 24.5 12 25 13 L27 20 L27 24 L5 24 Z" />
        <circle cx="10" cy="24" r="2.5" />
        <circle cx="22" cy="24" r="2.5" />
      </>
    ),
    coin: (
      <>
        <circle cx="16" cy="16" r="11" />
        <path d="M13 12 L19 12 M13 20 L19 20 M16 9 L16 23" />
      </>
    ),
    plus: <path d="M16 7 L16 25 M7 16 L25 16" />,
    minus: <path d="M7 16 L25 16" />,
    arrow: <path d="M7 16 L25 16 M19 10 L25 16 L19 22" />,
    check: <path d="M7 16 L14 23 L25 10" />,
    close: <path d="M9 9 L23 23 M23 9 L9 23" />,
    chevronRight: <path d="M12 8 L20 16 L12 24" />,
    chevronLeft: <path d="M20 8 L12 16 L20 24" />,
    chevronDown: <path d="M8 12 L16 20 L24 12" />,
    home: <path d="M5 15 L16 6 L27 15 L27 26 L19 26 L19 18 L13 18 L13 26 L5 26 Z" />,
    target: (
      <>
        <circle cx="16" cy="16" r="11" />
        <circle cx="16" cy="16" r="6" />
        <circle cx="16" cy="16" r="2" fill={color} stroke="none" />
      </>
    ),
    history: (
      <>
        <circle cx="16" cy="16" r="11" />
        <path d="M16 9 L16 16 L21 19" />
      </>
    ),
    sparkle: <path d="M16 6 L18 14 L26 16 L18 18 L16 26 L14 18 L6 16 L14 14 Z" />,
    transfer: (
      <>
        <path d="M5 12 L25 12 L21 8 M25 12 L21 16" />
        <path d="M27 20 L7 20 L11 24 M7 20 L11 16" />
      </>
    ),
    settings: (
      <>
        <circle cx="16" cy="16" r="4" />
        <path d="M16 3 L16 7 M16 25 L16 29 M3 16 L7 16 M25 16 L29 16 M7 7 L10 10 M22 22 L25 25 M7 25 L10 22 M22 10 L25 7" />
      </>
    ),
    logout: (
      <>
        <path d="M18 8 L18 5 L5 5 L5 27 L18 27 L18 24" />
        <path d="M12 16 L27 16 M22 11 L27 16 L22 21" />
      </>
    ),
    lock: (
      <>
        <rect x="7" y="14" width="18" height="14" rx="2" />
        <path d="M11 14 L11 10 C11 7 13 4 16 4 C19 4 21 7 21 10 L21 14" />
      </>
    ),
    piggy: (
      <>
        <path d="M5 16 C5 11 9 7 15 7 L20 7 C25 7 28 11 28 15 C28 17 27 18 26 19 L26 23 L23 23 L22 21 L14 21 L13 23 L10 23 L10 20 C7 19 5 17 5 16 Z" />
        <circle cx="11" cy="14" r="0.8" fill={color} stroke="none" />
        <path d="M18 11 L22 11" />
      </>
    ),
    trash: (
      <>
        <path d="M7 10 L25 10 L23 27 L9 27 Z" />
        <path d="M13 10 L13 7 L19 7 L19 10" />
        <path d="M4 10 L28 10" />
        <path d="M13 15 L13 22 M19 15 L19 22" />
      </>
    ),
    chart: (
      <>
        <path d="M5 26 L5 6 M5 26 L27 26" />
        <path d="M9 22 L13 17 L17 19 L22 11 L26 14" />
      </>
    ),
    mail: (
      <>
        <path d="M4 8 L28 8 L28 24 L4 24 Z" />
        <path d="M4 8 L16 17 L28 8" />
      </>
    ),
  };
  return <svg {...common}>{paths[name] ?? paths.coin}</svg>;
}
